import { DateTime } from "luxon"
import { StrictOmit } from "../../../../lib/lang"
import { ProductOrderDetails } from "../../../../shared/types/productAvailabilityTypes"
import { MonetaryAmount } from "../../../currency/currencyTypes"
import { ProductGroupOption } from "./components/FormikProductGroupField"
import { Shop } from "../shops/shopsTypes"
import { DeliveryLocation } from "../deliveryLocations/deliveryLocationsTypes"
import { ProductUserDetails } from "../priceList/priceListTypes"
import { PriceListType } from "../priceList/priceListApi"
import { ProductGroupCode, WeekDays } from "../productGroup/productGroupTypes"

export type BaseProduct = {
    name: string
    price: MonetaryAmount
    isActive: boolean
    isAdminOnly?: boolean
}

export type ProductsTableItem = Readonly<
    BaseProduct & {
        id: number
        productNumber?: string
        sortOrder?: number
        description?: string
        marketingDetails?: {
            isOffer?: boolean
        }
        productPhoto?: {
            uid?: string
            url: string
        }
        userDetails?: ProductUserDetails
        offerDetails?: OfferDetailsType
        productDescription?: {
            marketingTag: string
            allergens: Allergen[]
        }
        availabilityDetails?: AvailabilityDetails
        deadline?: Deadline
        priceExclVat?: MonetaryAmount
        priceInclVat?: MonetaryAmount
        stock?: ProductStockInfo
        orderDetails?: ProductOrderDetails
    }
>

export type ProductShop = StrictOmit<Shop, "isActive" | "uid">

export type Product = BaseProduct & {
    id: number
    merchantId: number
    productNumber: string
    description: string
    productPhoto?: {
        uid?: string
        url: string
    }
    productGroup: {
        id: number
        name: string
    }
    shops?: ProductShop[]
    productDescription?: {
        marketingTag: string
        allergens: Allergen[]
    }
    availabilityDetails?: AvailabilityDetails
    deadline?: Deadline
    offerDetails?: OfferDetailsType
    priceLists?: ProductPriceListRef[]
    barcode?: string
    paymentDetails?: {
        isPriceHidden?: boolean
        variablePrice?: boolean
    }
    securitySettings?: {
        isLocked: boolean
    }
    deliveryDetails?: {
        location: DeliveryLocation
    }
    itemLimits?: {
        maxDailyItemsPerPerson: number
        minItemsPerOrder: number
    }
    stock?: ProductStockInfo
}

export type CreateProduct = Readonly<
    StrictOmit<BaseProduct, "isActive" | "price"> & {
        price?: MonetaryAmount
        description?: string
        isActive?: boolean
        productNumber?: string
        productPhoto?: {
            uid?: string | null
            url?: string
        }
        productGroup: Record<"id", number>
        shops: Record<"id", number>[]
        productDescription?: {
            marketingTag: string
            allergens: {
                id: number
            }[]
        }
        availabilityDetails?: AvailabilityDetails
        deadline?: Deadline
        offerDetails?: OfferDetailsType
        priceLists?: { id: number; adjustedPrice?: MonetaryAmount }[]
        barcode?: string
        paymentDetails?: {
            isPriceHidden?: boolean
            variablePrice?: boolean
        }
        securitySettings?: {
            isLocked: boolean
        }
        deliveryDetails?: {
            location: { id: number }
        }
        itemLimits?: {
            maxDailyItemsPerPerson: number
            minItemsPerOrder: number
        }
        stock?: {
            // Optional for now — backend support for `isActive` is not ready yet.
            isActive?: boolean
            inventory?: StockUpdateInventory
            sales?: StockUpdateSales
        }
    }
>

export type AvailabilityDetails = Readonly<{
    dateInterval?: {
        from?: string
        to?: string
    }
    weekDays?: string[]
    timeInterval?: {
        start: string
        end: string
    }
    allowPreOrder: boolean
}>

export type Deadline = Readonly<{
    purchases: {
        daysBefore: number
        timeOfDay: string
    }
    cancellation?: {
        daysBefore: number
        timeOfDay: string
    }
}>

export type OfferDetailsType = Readonly<{
    discount?: {
        type: DiscountType
        percentage?: {
            amount: number
            scale: number
        }
        originalPrice?: MonetaryAmount
        discountedPrice?: MonetaryAmount
        discountAmount?: MonetaryAmount
        displayLabelType?: DisplayLabelType
    }
    productsOnSale?: number
    availabilityDetails?: {
        dateTimeInterval?: {
            from?: string
            to?: string
        }
    }
}>

export enum DiscountType {
    NONE = "NONE",
    DISCOUNT_PRICE = "DISCOUNT_PRICE",
    DISCOUNT_PERCENTAGE = "DISCOUNT_PERCENTAGE",
}

export type OfferDetailsFormValues = Readonly<{
    discount: {
        type: DiscountType
        percentage?: {
            amount: string
        }
        originalPrice?: number
        discountedPrice?: number
        discountAmount?: number
        displayLabelType?: DisplayLabelType
    }
    productsOnSale?: number
    availabilityDetails: {
        dateTimeInterval?: {
            from?: DateTime
            to?: DateTime
        }
    }
}>

export type PriceList = Readonly<{
    id: number
    name: string
    adjustedPrice: MonetaryAmount
}>

export type ProductPriceListRef = Readonly<{
    id: number
    name?: string
    adjustedPrice?: MonetaryAmount
}>

export type ProductGroupAvailability = Readonly<{
    weekDays: string[]
    timeInterval: {
        start: string
        end: string
    }
}>

export type ProductGroupSecurity = Readonly<{
    isLocked: boolean
}>

export type ProductGroups = Readonly<{
    id: number
    name: string
    sortOrder?: number
    isActive?: boolean
    products: ProductsTableItem[]
    availabilityDetails?: ProductGroupAvailability
    securitySettings?: ProductGroupSecurity
}>

export enum ProductTableRowType {
    GROUP = "group",
    PRODUCT = "product",
}

export type GroupItem = Readonly<
    {
        type: ProductTableRowType.GROUP
        id: number
        name: string
        isActive?: boolean
        groupAvailabilityDetails?: ProductGroupAvailability
        groupSecuritySettings?: ProductGroupSecurity
    } & Record<Exclude<keyof ProductsTableItem, "name" | "id" | "isActive">, undefined>
>

export type StockInfoFormValues = Readonly<{
    total: number | undefined
    inventory?: StockUpdateInventory
    inventoryIncomplete?: boolean
    sales?: StockUpdateSales
}>

export type ProductItem = Readonly<
    {
        type: ProductTableRowType.PRODUCT
        groupId?: number
    } & ProductsTableItem
>

export type ProductTableRow = GroupItem | ProductItem

export type CreateUpdateProductFormValues = Readonly<{
    productGroup: ProductGroupOption | null
    productGroupCode?: ProductGroupCode
    productNumber: string
    name: string
    price: number | undefined
    currency: string
    shops: ProductShop[]
    description: string
    isActive?: boolean
    isAdminOnly?: boolean
    productPhoto?: {
        uid?: string
        url: string
    }
    productDescription?: {
        marketingTag: string
        allergens: Allergen[]
    }
    availabilityDetails: {
        dateInterval?: {
            from?: DateTime | null
            to?: DateTime | null
        }
        weekDays: WeekDays[]
        timeInterval?: {
            start: DateTime | null
            end: DateTime | null
        }
        allowPreOrder: boolean
    }
    deadline?: {
        purchases: {
            daysBefore: number
            timeOfDay: DateTime
        }
        // hide for now
        // cancellation: {
        //     daysBefore: number
        //     timeOfDay: DateTime
        // }
    } | null
    offerDetails?: OfferDetailsType
    priceList: PriceListType | null
    // priceLists: PriceListType[]
    barcode?: string
    variablePrice: boolean
    isPriceHidden: boolean
    isLocked: boolean
    deliveryDetails?: {
        location: { id: number }
    }
    itemLimits?: {
        maxDailyItemsPerPerson: number
        minItemsPerOrder: number
    } | null
    itemsForSalePerDay?: number
    stock?: StockInfoFormValues | null
}>

export type Allergen = Readonly<{
    id: number
    name: string
}>

export enum DisplayLabelType {
    LIMITED_ON_SALE = "LIMITED_ON_SALE",
    SAVE_X = "SAVE_X",
    GREAT_OFFER = "GREAT_OFFER",
    BUY_NOW = "BUY_NOW",
    DAY_OFFER = "DAY_OFFER",
    WEEK_OFFER = "WEEK_OFFER",
    DISCOUNT = "DISCOUNT",
    GOOD_PRICE = "GOOD_PRICE",
    OLD_NEW_PRICE = "OLD_NEW_PRICE",
}

// -- Stock Management --

export enum StockAdjustmentType {
    INBOUND = "INBOUND",
    OUTBOUND = "OUTBOUND",
}

export enum StockTransactionType {
    INCREASE = "INCREASE",
    DECREASE = "DECREASE",
}

export const ADJUSTMENT_TO_TRANSACTION: Record<StockAdjustmentType, StockTransactionType> = {
    [StockAdjustmentType.INBOUND]: StockTransactionType.INCREASE,
    [StockAdjustmentType.OUTBOUND]: StockTransactionType.DECREASE,
}

export enum StockChangeReason {
    // Outbound
    SOLD = "SOLD",
    RESERVED = "RESERVED",
    SHIPPED = "SHIPPED",
    RETURNED_TO_VENDOR = "RETURNED_TO_VENDOR",
    CONSUMED_IN_PRODUCTION = "CONSUMED_IN_PRODUCTION",
    SCRAPPED = "SCRAPPED",
    LOST = "LOST",
    STOLEN = "STOLEN",
    SAMPLE_GIVEN = "SAMPLE_GIVEN",
    // Inbound
    PURCHASED = "PURCHASED",
    RETURNED_FROM_CUSTOMER = "RETURNED_FROM_CUSTOMER",
    PRODUCED = "PRODUCED",
    FOUND_DURING_COUNT = "FOUND_DURING_COUNT",
    TRANSFERRED_IN = "TRANSFERRED_IN",
}

export const OUTBOUND_REASONS: StockChangeReason[] = [
    StockChangeReason.SOLD,
    StockChangeReason.RESERVED,
    StockChangeReason.SHIPPED,
    StockChangeReason.RETURNED_TO_VENDOR,
    StockChangeReason.CONSUMED_IN_PRODUCTION,
    StockChangeReason.SCRAPPED,
    StockChangeReason.LOST,
    StockChangeReason.STOLEN,
    StockChangeReason.SAMPLE_GIVEN,
]

export const INBOUND_REASONS: StockChangeReason[] = [
    StockChangeReason.PURCHASED,
    StockChangeReason.RETURNED_FROM_CUSTOMER,
    StockChangeReason.PRODUCED,
    StockChangeReason.FOUND_DURING_COUNT,
    StockChangeReason.TRANSFERRED_IN,
]

export type ProductStockInfo = Readonly<{
    isActive?: boolean
    inventory: {
        total: number
        sold?: number
        reserved?: number
        available?: number
    }
    sales?: {
        daily?: {
            itemsForSale: number
            sold: number
            remaining: number
        }
        history?: unknown[]
    }
    alerts?: {
        minStock: number
    }
}>

export type StockUpdateInventory = Readonly<{
    stockUpdate: {
        transactionType: StockTransactionType
        quantityDelta: number
        reason: StockChangeReason
        note?: string
    }
}>

export type StockUpdateSales = Readonly<{
    daily: {
        itemsForSale: number
    }
}>

export type UpdateStockPayload = Readonly<{
    productId: number
    inventory?: StockUpdateInventory
    sales?: StockUpdateSales
}>

export type StockPopoverFormValues = Readonly<{
    type: StockAdjustmentType
    quantityDelta: string
    reason: StockChangeReason | ""
    note: string
}>

export type ItemsPerDayFormValues = Readonly<{
    itemsForSale: string
}>

export type CombinedStockFormValues = Readonly<{
    type: StockAdjustmentType
    quantityDelta: string
    reason: StockChangeReason | ""
    note: string
    itemsForSale: string
}>
